import { useMemo } from "react";
import {
    type AnyLexicalExtensionArgument,
    defineExtension,
} from "lexical";
import { LexicalExtensionComposer } from "@lexical/react/LexicalExtensionComposer";
import { ContentEditable } from "@lexical/react/LexicalContentEditable";

import { BaseExtension } from "./extensions/baseExtension";
import { HtmlSync, type EmptyBlockType } from "./components/HtmlSync";
import { TableCellResizer } from "./components/TableCellResizer";
import { TableSelectHandle } from "./components/TableSelectHandle";
import { Toolbar } from "./components/Toolbar/Toolbar";
import "./theme.css";

export interface MotiRichTextEditorProps {
    value: string;
    onChange: (html: string) => void;
    placeholder?: string;
    extensions?: AnyLexicalExtensionArgument[];
    emptyBlockType?: EmptyBlockType;
}

export function MotiRichTextEditor({
    value,
    onChange,
    placeholder = "",
    extensions,
    emptyBlockType,
}: MotiRichTextEditorProps) {
    const rootExtension = useMemo(
        () =>
            defineExtension({
                name: "@motimatic/rich-text-editor/root",
                dependencies: [BaseExtension, ...(extensions ?? [])],
            }),
        [extensions],
    );

    return (
        <LexicalExtensionComposer extension={rootExtension} contentEditable={null}>
            <div className="rte-shell">
                <Toolbar />
                <div className="rte-content-wrapper">
                    <ContentEditable
                        className="rte-content"
                        aria-placeholder={placeholder}
                        placeholder={
                            <div className="rte-placeholder">{placeholder}</div>
                        }
                    />
                    <TableCellResizer />
                    <TableSelectHandle />
                </div>
                <HtmlSync value={value} onChange={onChange} emptyBlockType={emptyBlockType} />
            </div>
        </LexicalExtensionComposer>
    );
}
